import { useState } from "react";
import { useMediaQuery } from "react-responsive";
import { format } from "date-fns";
import clsx from "clsx";

import { AttachmentModel, TransactionModel } from "@interfaces/index";
import { QUERY_WIDTHS, formatAmount } from "@utils/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  Drawer,
  DrawerHeader,
  DrawerScrollableContainer,
  TrashSvg,
  XSvg,
  TransactionStatus,
  SubmissionStatus,
  Button,
  EditSvg,
  ArchiveSvg,
  ReceiptRefundSvg,
  ConfirmModal,
  FileItem,
  FilePreview,
} from "@components/index";

export const TransactionDetails = ({
  transaction,
  onRemove,
  onClose,
}: {
  transaction?: TransactionModel;
  onRemove: () => void;
  onClose: () => void;
}) => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const setCommonState = useCommonStore(({ setState }) => setState);
  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const [attachmentPreview, setAttachmentPreview] = useState<AttachmentModel>();

  const [showConfirm, setShowConfirm] = useState(false);

  const handleRemove = () => {
    setShowConfirm(false);
    if (onRemove) {
      setCommonState({ isDrawerShown: false });
      onRemove();
    }
  };

  const handleClose = () => {
    setCommonState({ isDrawerShown: false });
    onClose();
  };

  return (
    transaction && (
      <Drawer>
        <DrawerHeader
          onClose={handleClose}
          title={
            <div className="text-300 font-bold text-black-100 md:text-600">
              {transaction.userBusiness.name}
            </div>
          }
          rightContent={
            transaction.policies.canDelete && (
              <button
                type="button"
                className="flex items-center gap-x-2 text-black-54 hover:opacity-80"
                onClick={() => setShowConfirm(true)}
                data-testid="button_removeTransaction"
              >
                <TrashSvg />
                {!isMobile && "Delete"}
              </button>
            )
          }
        />
        <div className="flex items-center gap-x-3 px-4 pt-6 md:px-14 md:pt-0">
          <TransactionStatus status={transaction.status} />
          {transaction.refund && (
            <div className="flex items-center gap-x-0.5 rounded-2xl bg-orange-8 px-3 py-1 text-button text-orange-100">
              <div className="*:size-[20px]">
                <ReceiptRefundSvg />
              </div>
              <div>Refund</div>
            </div>
          )}
        </div>
        <DrawerScrollableContainer>
          <div className="pb-4 pt-10 text-300 font-bold text-black-100 md:text-400">
            Transaction information
          </div>
          <div className="flex flex-col gap-x-6 gap-y-2 border-b border-b-black-16 pb-4 pt-4 md:flex-row md:pb-6">
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              Type
            </div>
            <div className="capitalize text-black-100 md:text-300">
              {transaction.type}
            </div>
          </div>
          <div className="flex flex-col gap-x-6 gap-y-2 border-b border-b-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              Date
            </div>
            <div className="text-black-100 md:text-300">
              {format(new Date(transaction.date), "dd.MM.yyyy")}
            </div>
          </div>
          {transaction.isImported && (
            <div className="flex flex-col gap-x-6 gap-y-2 border-b border-b-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
              <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
                External Category
              </div>
              <div className="capitalize text-black-100 md:text-300">
                {transaction.externalCategoryTitle || "Unmapped Category"}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-x-6 gap-y-2 border-b border-b-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              Category
            </div>
            <div className="capitalize text-black-100 md:text-300">
              {transaction.category.title}
            </div>
          </div>
          <div className="flex flex-col gap-x-6 gap-y-2 border-b border-b-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              Subcategory
            </div>
            <div className="capitalize text-black-100 md:text-300">
              {transaction.subcategory.title}
            </div>
          </div>
          {!disabledFeatures.includes("Assets") && !!transaction.asset && (
            <div className="flex flex-col gap-x-6 gap-y-2 border-b border-b-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
              <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
                Asset
              </div>
              <div className="capitalize text-black-100 md:text-300">
                {transaction.asset.name}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-x-6 gap-y-2 border-b border-b-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              Period
            </div>
            <div className="capitalize text-black-100 md:text-300">
              {transaction.period.title}
            </div>
          </div>
          <div className="flex flex-col gap-x-6 gap-y-2 border-b border-b-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              Submition status
            </div>
            <div className="capitalize text-black-100 md:text-300">
              <SubmissionStatus status={transaction.submissionStatus} />
            </div>
          </div>
          <div className="flex flex-col gap-x-6 gap-y-2 border-b border-b-black-16 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6">
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              Amount
            </div>
            <div className="capitalize text-black-100 md:text-300">
              {formatAmount(transaction.amount)}
            </div>
          </div>
          {transaction.disallowed && (
            <div className="border-b border-b-black-16 pb-4 pt-4 text-black-100 md:pb-6 md:pt-6 md:text-300">
              This is a disallowed transaction and will not be included in your
              calculations.
            </div>
          )}
          <div
            className={clsx(
              "flex flex-col gap-x-6 gap-y-2 pb-4 pt-4 md:flex-row md:pb-6 md:pt-6",
              !!transaction.attachments?.length && "border-b border-b-black-16",
            )}
          >
            <div className="text-black-70 md:w-60 md:shrink-0 md:text-300">
              Description
            </div>
            <div className="capitalize text-black-100 md:text-300">
              {transaction.description}
            </div>
          </div>
          {!transaction.attachments?.length ? null : (
            <>
              <div className="pb-8 pt-10 text-300 font-bold text-black-100 md:pb-6 md:text-400">
                Transaction Receipts
              </div>
              <div className="flex flex-col gap-y-4">
                {transaction.attachments.map((attachment) => (
                  <FileItem
                    key={attachment.uniqueId}
                    file={attachment}
                    onPreview={setAttachmentPreview}
                    isEditing={false}
                  />
                ))}
              </div>
            </>
          )}
          <div className="mt-10 flex flex-col gap-y-4 border-t border-t-black-8 pb-2 md:flex-row md:justify-between md:pb-4 md:pt-4">
            <div className="flex flex-col gap-x-4 gap-y-4 md:flex-row">
              <Button
                isLink
                href={`/transactions/update/${transaction.uniqueId}`}
                leftIcon={<EditSvg />}
                title={
                  transaction.refund
                    ? "Edit Refund Transaction"
                    : "Edit Transaction"
                }
                disabled={!transaction.policies.canUpdate}
                dataTestid="button_editTransaction"
                height="small"
              />
              {!transaction.refund && (
                <Button
                  isLink
                  href={`/transactions/refund/${transaction.uniqueId}`}
                  variant="secondary"
                  leftIcon={<ArchiveSvg />}
                  title="Add Refund"
                  height="small"
                  dataTestid="button_addRefund"
                />
              )}
            </div>
            <Button
              variant="secondary"
              leftIcon={<XSvg />}
              title="Cancel"
              height="small"
              onClick={handleClose}
            />
          </div>
        </DrawerScrollableContainer>
        <FilePreview
          attachment={attachmentPreview}
          onClose={() => setAttachmentPreview(undefined)}
        />
        <ConfirmModal
          show={showConfirm}
          confirmButtonTitle="Delete"
          text1="Are you sure?"
          text2="Do you really want to delete this transaction?"
          onClickCancel={() => setShowConfirm(false)}
          onClickConfirm={handleRemove}
        />
      </Drawer>
    )
  );
};
